import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled/macro";
import { Accordion, Autocomplete, createStyles, Spoiler, Text, useMantineTheme } from "@mantine/core";
import { AssessmentScenarioData } from "../../../../../types/assessments";
import { BehaviorParameter, BehaviorParameterTemplate } from "../../../../../types/behaviors";
import { Optional } from "../../../../../types/common";
import { Echelon, ParentEchelon } from "../../../../../types/force-packages";
import { InputData } from "../../../../../types/systems";
import BehaviorNestedAccordionControl from "./BehaviorNestedAccordionControl";
import BehaviorPanel from "./BehaviorPanel";
import BehaviorSystemElement from "./BehaviorSystemElement";

interface IBehaviorNestedAccordions {
	forcePackage: ParentEchelon;
	scenarioSettings: Optional<AssessmentScenarioData, "id">;
	pathToElement: number[];
	inputData: InputData;
	behaviorSettingsProperty: "blue_behavior_settings" | "red_behavior_settings";
	echelonSettingsProperty: "blue_echelon_settings" | "red_echelon_settings";
	openedAccordionsNames: string[]; // to maintain the state of opened accordions after re-render (reopening drawer with accordions for example)
	visibleEchelons: number[];
	isTopLevel?: boolean;
	topLevelSettings?: BehaviorParameterTemplate[];
	currentEchelon?: Echelon;
	searchedPhrase?: string; // for searching echelons and systems by name/id
	setVisibleEchelons: (echelon: Echelon, checked: boolean) => void;
	setSetting: (settingName: string, value: AssessmentScenarioData[keyof AssessmentScenarioData]) => void;
	updateSelectedSymbolId: (id: number | null) => void;
	updateSelectedEchelonId: (id: number | null) => void;
	setOpenedAccordionsNames: (name: string[]) => void;
}

const BehaviorNestedAccordions: React.FC<IBehaviorNestedAccordions> = ({
	forcePackage,
	scenarioSettings,
	isTopLevel = false,
	pathToElement,
	inputData,
	behaviorSettingsProperty,
	echelonSettingsProperty,
	topLevelSettings,
	currentEchelon,
	openedAccordionsNames,
	searchedPhrase = "",
	visibleEchelons,
	setVisibleEchelons,
	setSetting,
	updateSelectedSymbolId,
	updateSelectedEchelonId,
	setOpenedAccordionsNames,
}) => {
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const transitionDuration = useMemo(() => 500, []);
	const [value, setValue] = useState<string[]>([]);
	const [currentForcePackage, setCurrentForcePackage] = useState<ParentEchelon>(currentEchelon || forcePackage);
	const [searchedPhraseState, setsearchedPhraseState] = useState<string>(searchedPhrase || "");

	// After closing the item there would be jump because of conditional rendering immediately removing the echelon name from the value state
	// so we setTimeout for setting itemsRenderPanelValues corresponding echelon name property to false for the transition duration to prevent this
	const [itemsRenderPanelValues, setItemsRenderPanelValues] = useState<Record<string, boolean>>(
		currentForcePackage.echelons.reduce(
			(acc, echelon, ix) => {
				acc[`${echelon.name}${ix}`] = isTopLevel ? true : false;
				return acc;
			},
			{} as Record<string, boolean>
		)
	);

	useEffect(() => {
		if (openedAccordionsNames.length > 0) setValue(openedAccordionsNames);
	}, []);

	useEffect(() => {
		if (currentEchelon) setCurrentForcePackage(currentEchelon);
	}, [currentEchelon]);

	useEffect(() => {
		if (currentForcePackage) {
			isTopLevel && setValue([currentForcePackage.name + "0"]);
		}
	}, [currentForcePackage, isTopLevel]);

	useEffect(() => {
		setsearchedPhraseState(searchedPhrase);
	}, [searchedPhrase]);

	const setEchelonSetting = (value: Record<number, BehaviorParameter[]> | null, propertyName: string) => {
		if (value) setSetting(propertyName, value);
	};

	const handleOnChange = (val: string[]) => {
		if (isTopLevel) {
			if (val.filter((name) => !name.includes("system")).length === 0) {
				return;
			}
		}
		if (val.length < value.length) {
			const name = value.filter((name) => !val.includes(name))[0];
			setTimeout(() => {
				setItemsRenderPanelValues((current) => {
					const newItemsRenderPanelValues = { ...current };
					newItemsRenderPanelValues[name] = false;
					return newItemsRenderPanelValues;
				});
			}, transitionDuration);
		} else {
			const name = val.filter((name) => !value.includes(name))[0];
			setItemsRenderPanelValues((current) => {
				const newItemsRenderPanelValues = { ...current };
				newItemsRenderPanelValues[name] = true;
				return newItemsRenderPanelValues;
			});
		}
		setOpenedAccordionsNames(val);
		setValue(val);
	};

	// recursively get unique echelon and system names from the force package
	const getUniqueEchelonAndSystemNames = (forcePackage: ParentEchelon | Echelon) => {
		const uniqueNames: string[] = [];

		forcePackage.echelons.forEach((echelon) => {
			if (!uniqueNames.includes(echelon.name)) {
				uniqueNames.push(echelon.name);
			}
			echelon.systems.forEach((system) => {
				const systemName = scenarioSettings.systems[system.system_id].name;
				if (!uniqueNames.includes(systemName)) {
					uniqueNames.push(systemName);
				}
			});
			if (echelon.echelons && echelon.echelons.length > 0) {
				uniqueNames.push(...getUniqueEchelonAndSystemNames(echelon));
			}
		});

		return uniqueNames;
	};

	// Function to check if the name is present down the hierarchy of an echelon (echelon name, system names, etc.)
	const containsNameOrId = (echelon: Echelon, name: string) => {
		const checkEchelon = (echelon: Echelon): boolean => {
			if (echelon.name.includes(name) || echelon.id.toString() === name) return true;
			if (
				echelon.systems.some(
					(system) => scenarioSettings.systems[system.system_id].name.includes(name) || system.agent_id.toString() === name
				)
			)
				return true;
			return echelon.echelons.some(checkEchelon);
		};

		return checkEchelon(echelon);
	};

	return (
		<>
			{isTopLevel ? (
				<Autocomplete
					data={getUniqueEchelonAndSystemNames(forcePackage.echelons[0])}
					placeholder="Search"
					mb={"1rem"}
					value={searchedPhraseState}
					onChange={(value) => {
						setsearchedPhraseState(value);
					}}
				></Autocomplete>
			) : null}

			<Accordion
				className={classes.accordionContainer}
				defaultValue={value}
				value={value}
				mt={isTopLevel ? "0" : "1rem"}
				chevronSize={isTopLevel ? "0" : "1rem"}
				transitionDuration={transitionDuration}
				multiple
				onChange={handleOnChange}
			>
				{currentForcePackage
					? currentForcePackage.echelons.map((echelon, echelonIx) => {
							return (
								<AccordionItem
									key={echelonIx}
									value={`${echelon.name}${echelonIx}`}
									className={isTopLevel ? classes.topLevelAccordionItem : ""}
									style={{
										display:
											searchedPhraseState.length > 0
												? containsNameOrId(echelon, searchedPhraseState)
													? "block"
													: "none"
												: "block",
									}}
								>
									<BehaviorNestedAccordionControl
										parentEchelon={currentForcePackage.echelons[0]}
										echelon={echelon}
										isTopLevel={isTopLevel}
										settings={scenarioSettings}
										inputData={inputData}
										behaviorSettingsProperty={behaviorSettingsProperty}
										visibleEchelons={visibleEchelons}
										updateSelectedEchelonId={updateSelectedEchelonId}
										setVisibleEchelons={setVisibleEchelons}
										setSetting={(value, duplicate?, changeTopLevelName?) =>
											setEchelonSetting(value, behaviorSettingsProperty)
										}
									/>

									<AccordionPanel>
										{/* Conditional panel rendering to prevent from loading all contents at once */}
										{value.includes(`${echelon.name}${echelonIx}`) ||
										itemsRenderPanelValues[`${echelon.name}${echelonIx}`] ? (
											<>
												<Spoiler
													maxHeight={0}
													showLabel="Show echelon settings"
													hideLabel="Hide"
													style={{ paddingTop: "1rem" }}
												>
													<BehaviorPanel
														entityId={echelon.id}
														behaviorSettings={scenarioSettings[echelonSettingsProperty]}
														inputData={inputData}
														scenarioSettings={scenarioSettings}
														setSetting={(value, duplicate?, changeTopLevelName?) =>
															setEchelonSetting(value, echelonSettingsProperty)
														}
														systemId={0}
														topLevelSettings={topLevelSettings}
														isEditionModeOn={false}
														isEchelonLevel
														preview
													></BehaviorPanel>
												</Spoiler>

												<BehaviorNestedAccordions
													forcePackage={forcePackage}
													scenarioSettings={scenarioSettings}
													isTopLevel={false}
													setSetting={setSetting}
													pathToElement={[...pathToElement, echelonIx]}
													inputData={inputData}
													behaviorSettingsProperty={behaviorSettingsProperty}
													echelonSettingsProperty={
														behaviorSettingsProperty.replace("behavior_settings", "echelon_settings") as
															| "blue_echelon_settings"
															| "red_echelon_settings"
													}
													currentEchelon={echelon as Echelon}
													searchedPhrase={searchedPhraseState}
													visibleEchelons={visibleEchelons}
													setVisibleEchelons={setVisibleEchelons}
													updateSelectedSymbolId={updateSelectedSymbolId}
													updateSelectedEchelonId={updateSelectedEchelonId}
													openedAccordionsNames={openedAccordionsNames}
													setOpenedAccordionsNames={setOpenedAccordionsNames}
													topLevelSettings={topLevelSettings}
												></BehaviorNestedAccordions>

												{echelon.type !== 1 && echelon.systems.length > 0 && (
													<Text className={classes.commanderText}>
														Commanders<hr></hr>
													</Text>
												)}

												<SystemsAccordion multiple defaultValue={value} value={value} onChange={handleOnChange}>
													{echelon.systems.map((system, systemIx) => {
														const isDark = theme.colorScheme === "dark";
														const color =
															(scenarioSettings.systems[system.system_id].side as number) === 0
																? isDark
																	? theme.colors.blue[4]
																	: theme.colors.blue[8]
																: isDark
																	? theme.colors.accentRed[4]
																	: theme.colors.accentRed[5];

														return (
															<BehaviorSystemElement
																key={systemIx}
																systemIx={systemIx}
																color={color}
																inputData={inputData}
																systemId={system.system_id}
																agentId={system.agent_id}
																scenarioSettings={scenarioSettings}
																behaviorSettings={scenarioSettings[behaviorSettingsProperty]}
																isCommander={echelon.type !== 1}
																echelon={echelon}
																setSetting={(value, duplicate?, changeTopLevelName?) =>
																	setEchelonSetting(value, behaviorSettingsProperty)
																}
																updateSelectedSymbolId={updateSelectedSymbolId}
															/>
														);
													})}
												</SystemsAccordion>
											</>
										) : null}
									</AccordionPanel>
								</AccordionItem>
							);
						})
					: null}
			</Accordion>
		</>
	);
};

export default BehaviorNestedAccordions;

const useStyles = createStyles((theme) => ({
	accordionContainer: {
		width: "100%",
		borderBottom: "none",

		"& > :not(:first-of-type)": {
			marginTop: "1rem",
		},
	},

	topLevelAccordionItem: {
		backgroundColor: theme.colors.appMainColor[5],
	},

	commanderText: {
		textAlign: "center",
		marginTop: "1rem",
		color: theme.colorScheme === "dark" ? theme.colors.dark[3] : theme.colors.dark[3],

		"& > hr": {
			margin: "0.5rem 0",
			backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.light[5],
			border: "none",
			padding: "1px",
		},
	},
}));

const SystemsAccordion = styled(Accordion)``;

const AccordionItem = styled(Accordion.Item)`
	border: ${({ theme }) => `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.light[5]}`};
`;

const AccordionPanel = styled(Accordion.Panel)`
	& .mantine-Accordion-content {
		padding: 0 1rem 1rem 1rem;
	}
`;
